<template>
	<div :class="$attrs.class" :style="$attrs.style"
		@click.capture="onRowClickCapture">
		<BootstrapVueNextTable
			v-bind="tableAttrs"
			ref="table"
			v-model:sort-by="sortByModel"
			@head-clicked="onHeadClicked"
			@sorted="onSorted">
			<template v-for="(_, name) in $slots" #[name]="slotProps">
				<slot :name="name" v-bind="slotProps"></slot>
			</template>
			<template v-if="!$slots[sortAscSlot]" #[sortAscSlot]>
				<span class="b-table-sort-icon b-table-sort-icon-asc" aria-hidden="true">
					<span class="b-table-sort-caret b-table-sort-caret-up"></span>
					<span class="b-table-sort-caret b-table-sort-caret-down"></span>
				</span>
			</template>
			<template v-if="!$slots[sortDescSlot]" #[sortDescSlot]>
				<span class="b-table-sort-icon b-table-sort-icon-desc" aria-hidden="true">
					<span class="b-table-sort-caret b-table-sort-caret-up"></span>
					<span class="b-table-sort-caret b-table-sort-caret-down"></span>
				</span>
			</template>
			<template v-if="!$slots[sortDefaultSlot]" #[sortDefaultSlot]>
				<span class="b-table-sort-icon b-table-sort-icon-default" aria-hidden="true">
					<span class="b-table-sort-caret b-table-sort-caret-up"></span>
					<span class="b-table-sort-caret b-table-sort-caret-down"></span>
				</span>
			</template>
		</BootstrapVueNextTable>
	</div>
</template>

<script>
	import { BTable as BootstrapVueNextTable } from 'bootstrap-vue-next';

	export default {
		name: 'BTable',
		// Written for Vue 3 (listeners, class and style in $attrs), so skip the Vue 2 compat rewrites
		compatConfig: { MODE: 3 },
		components: {
			BootstrapVueNextTable,
		},
		inheritAttrs: false,
		props: {
			sortBy: {
				type: [Array, Object, String],
				default: undefined,
			},
			sortDesc: {
				type: Boolean,
				default: false,
			},
		},
		emits: [
			'head-clicked',
			'row-clicked',
			'sort-changed',
			'sorted',
			'update:sortBy',
		],
		data() {
			return {
				localSortBy: this.normalizeSort(this.sortBy),
			}
		},
		computed: {
			sortAscSlot() {
				return 'sortAsc()';
			},
			sortDescSlot() {
				return 'sortDesc()';
			},
			sortDefaultSlot() {
				return 'sortDefault()';
			},
			tableAttrs() {
				const tbodyTrAttrs = this.$attrs['tbody-tr-attrs']
					|| this.$attrs.tbodyTrAttrs
					|| this.$attrs['tbody-tr-attr']
					|| this.$attrs.tbodyTrAttr;
				const tbodyTrClass = this.$attrs['tbody-tr-class'] || this.$attrs.tbodyTrClass;

				// class and style go on the wrapper div
				const attrs = { ...this.$attrs };
				delete attrs.class;
				delete attrs.style;

				return {
					...attrs,
					...(tbodyTrAttrs ? { tbodyTrAttrs } : {}),
					...(tbodyTrClass ? { tbodyTrClass } : {}),
					...(typeof this.$attrs.provider === 'function' ? { provider: this.itemsProvider } : {}),
				};
			},
			sortByModel: {
				get() {
					return this.localSortBy;
				},
				set(value) {
					this.localSortBy = this.normalizeSort(value);
					this.$emit('update:sortBy', this.localSortBy);
				},
			},
			tableItems() {
				return Array.isArray(this.tableAttrs.items) ? this.tableAttrs.items : [];
			},
		},
		watch: {
			sortBy(value) {
				this.localSortBy = this.normalizeSort(value);
			},
			sortDesc() {
				this.localSortBy = this.normalizeSort(this.sortBy);
			},
		},
		methods: {
			normalizeSort(sortBy) {
				if (Array.isArray(sortBy)) {
					const normalizedSort = sortBy
						.map((sort) => {
							const key = sort?.key || sort?.sortBy;
							const order = sort?.order || (sort?.sortDesc ? 'desc' : undefined);

							return key ? { key, order } : null;
						})
						.filter(Boolean);
					const activeSort = normalizedSort.filter((sort) => sort.order);

					return activeSort.length ? activeSort : normalizedSort;
				}

				if (sortBy && typeof sortBy === 'object') {
					const key = sortBy.key || sortBy.sortBy;

					return key ? [{ key, order: sortBy.order || (sortBy.sortDesc ? 'desc' : 'asc') }] : [];
				}

				return sortBy ? [{ key: sortBy, order: this.sortDesc ? 'desc' : 'asc' }] : undefined;
			},
			itemsProvider(ctx) {
				return this.$attrs.provider({
					...ctx,
					sortBy: this.normalizeSort(ctx.sortBy),
				});
			},
			sortSignature(sortBy = this.localSortBy) {
				return JSON.stringify(this.normalizeSort(sortBy) || []);
			},
			sortChangedPayload(sortBy) {
				const normalizedSort = this.normalizeSort(sortBy);

				return {
					sortBy: normalizedSort?.[0]?.key,
					sortDesc: normalizedSort?.[0]?.order === 'desc',
				};
			},
			emitSortEvents(sortBy, originalSort = sortBy) {
				const normalizedSort = this.normalizeSort(sortBy);

				this.$emit('sorted', normalizedSort?.[0] || originalSort);
				this.$emit('sort-changed', this.sortChangedPayload(normalizedSort));
			},
			applyHeaderSort(fieldKey) {
				const currentSort = this.normalizeSort(this.localSortBy) || [];
				const currentFieldSort = currentSort.find((sort) => sort.key === fieldKey);
				const order = currentFieldSort?.order === 'asc' ? 'desc' : 'asc';
				const nextSort = [{ key: fieldKey, order }];

				this.localSortBy = nextSort;
				this.$emit('update:sortBy', nextSort);
				this.emitSortEvents(nextSort);
				this.$nextTick(() => this.refresh());
			},
			queueHeaderSort(fieldKey, field, event, isFooter = false) {
				const previousSortSignature = this.sortSignature();

				this.$emit('head-clicked', fieldKey, field, event, isFooter);

				this.$nextTick(() => {
					if (isFooter || !field?.sortable) {
						return;
					}

					if (previousSortSignature === this.sortSignature()) {
						this.applyHeaderSort(fieldKey);
					}
				});
			},
			onRowClickCapture(event) {
				const row = event.target?.closest?.('tbody tr');
				if (!row || !event.currentTarget?.contains(row)) return;
				if (this.shouldIgnoreRowClick(event)) return;

				const rows = [...row.parentElement.children].filter((el) => el.matches('tr'));
				const rowIndex = rows.indexOf(row);
				const item = this.tableItems[rowIndex];
				if (item) {
					this.$emit('row-clicked', item, rowIndex, event);
				}
			},
			shouldIgnoreRowClick(event) {
				// don't fire row-clicked when the actual click on button
				return !!event?.target?.closest?.('button, input, select, textarea, .dropdown-menu, [role="button"]');
			},
			onHeadClicked(fieldKey, field, event, isFooter) {
				this.queueHeaderSort(fieldKey, field, event, isFooter);
			},
			onSorted(sort) {
				this.emitSortEvents(sort, sort);
			},
			refresh() {
				return this.$refs.table?.refresh?.();
			},
			selectAllRows() {
				return this.$refs.table?.selectAllRows?.();
			},
			clearSelected() {
				return this.$refs.table?.clearSelected?.();
			},
			selectRow(index) {
				return this.$refs.table?.selectRow?.(index);
			},
			unselectRow(index) {
				return this.$refs.table?.unselectRow?.(index);
			},
			isRowSelected(index) {
				return this.$refs.table?.isRowSelected?.(index);
			},
		},
	}
</script>
